import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import keycloak from '../config/keycloak';
import env from '../config/env';
import LoadingSpinner from '../components/LoadingSpinner';

const KeycloakContext = createContext(null);

export const KeycloakProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const isInitialized = useRef(false);
    const useKeycloakAuth = env.useKeycloakAuth;

    useEffect(() => {
        const initKeycloak = async () => {
            // If not using Keycloak auth, just set loading to false
            if (!useKeycloakAuth) {
                setIsLoading(false);
                return;
            }

            // If already initialized, don't initialize again
            if (isInitialized.current) {
                return;
            }

            try {
                console.log('Initializing Keycloak...');
                isInitialized.current = true;
                const authenticated = await keycloak.init({
                    onLoad: 'check-sso',
                    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
                    pkceMethod: 'S256',
                    checkLoginIframe: false
                });
                console.log('Keycloak initialized, authenticated:', authenticated);
                setIsAuthenticated(authenticated);
            } catch (error) {
                console.error('Failed to initialize Keycloak:', error);
                isInitialized.current = false;
            } finally {
                setIsLoading(false);
            }
        };

        initKeycloak();
    }, [useKeycloakAuth]);

    const login = () => {
        if (useKeycloakAuth && !isAuthenticated) {
            console.log('Initiating Keycloak login...');
            keycloak.login({
                redirectUri: window.location.origin
            });
        }
    };

    const logout = () => {
        if (useKeycloakAuth) {
            keycloak.logout({
                redirectUri: window.location.origin
            });
        }
    };

    const getToken = () => keycloak.token;

    const contextValue = {
        keycloak,
        isAuthenticated,
        isLoading,
        login,
        logout,
        getToken
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <KeycloakContext.Provider value={contextValue}>
            {children}
        </KeycloakContext.Provider>
    );
};

export const useKeycloak = () => {
    const context = useContext(KeycloakContext);
    if (!context) {
        throw new Error('useKeycloak must be used within a KeycloakProvider');
    }
    return context;
}; 